/**
 * nadine-hauswirth.com's Chakra v3 system, in two flavours.
 *
 * `system` is what the site's own routes mount, through the branch in
 * gatsby-plugin-jaen's Layout. It carries the site's global styles.
 *
 * `chromeSystem` is the same tokens with no globals, and it exists for
 * SearchMenu, which is mounted through the Toolbar shadow INSIDE jaen's frame
 * and therefore also renders on CMS routes. v2 gave it a bare <ThemeProvider>,
 * which re-provided variables and nothing else. In v3 a provider is also the
 * global-style emitter, so handing SearchMenu the full `system` would push the
 * site's html and body rules onto every CMS route, which v2 never did.
 *
 * The prefix stays `chakra` and jaen takes `jaen`. Two systems with disjoint
 * prefixes cannot collide whatever selector they land on, which is what
 * replaces v2's cssVarsRoot scoping. cssVarsRoot itself is not an option: in
 * v3 it only applies to the base bucket, `_dark` always lands on the raw
 * selector, and dark mode inside the CMS dies with it.
 */
import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

import themeColors from './colors';
import themeFonts from './fonts';
import {
  accordionSlotRecipe,
  alertSlotRecipe,
  breadcrumbSlotRecipe,
  buttonRecipe,
  containerRecipe,
  headingRecipe,
  kbdRecipe,
  linkRecipe,
  listSlotRecipe,
  separatorRecipe,
  skeletonRecipe,
  tooltipSlotRecipe
} from './recipes';
import themeSemanticTokens from './semanticTokens/semanticTokens';
import { toV3SemanticTokens } from './semanticTokens/to-v3';

/**
 * The base colour scales the semantic layer references. These must be
 * registered before anything else, because an unresolvable `{colors.x.y}` is
 * not an error in v3: it emits `--var: colors\.x\.y`, a well-formed
 * declaration full of garbage. Miss `gray.550` or `pq.500` and a dozen tokens
 * die without a word.
 */
type TokenTree = { value: string } | { [key: string]: TokenTree };

const toTokenScale = (value: unknown): TokenTree =>
  typeof value === 'string'
    ? { value }
    : Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [
          k,
          toTokenScale(v)
        ])
      );

/**
 * The eight slots `colorPalette` resolves against, filled for `brand`.
 *
 * `html { colorPalette: 'brand' }` below is this migration's replacement for
 * v2's `withDefaultColorScheme({colorScheme: 'brand'})`, and on its own it is
 * inert: it maps the numeric ramp and nothing else, while every v3 recipe reads
 * the eight NAMED slots. Each unfilled slot emits
 * `var(--chakra-colors-color-palette-solid)` against a variable no rule
 * defines. A missing custom property is invalid at computed-value time, not an
 * error, so tsc and gatsby build stay green while solid buttons lose their
 * white label, focus rings lose their colour, and the site goes grey.
 *
 * The light half of each slot is what v2's brand-schemed component produced,
 * read off @chakra-ui/theme rather than chosen:
 *
 *   solid     Button variantSolid  bg         mode(`${c}.500`, `${c}.200`)
 *   contrast  Button variantSolid  color      mode('white', 'gray.800')
 *   fg        Button variantGhost  color      mode(`${c}.600`, `${c}.200`)
 *   subtle    Button variantGhost  _hover.bg  mode(`${c}.50`,  transparentize…)
 *   muted     Button variantGhost  _active.bg mode(`${c}.100`, transparentize…)
 *
 * The dark halves of `subtle` and `muted` are NOT v2's: v2 used
 * `transparentize(`${c}.200`, 0.12/0.24)`, an alpha blend with no token
 * spelling, and the steps here are the nearest opaque equivalent.
 * `emphasized`, `border` and `focusRing` have no v2 origin at all and follow
 * jaen's own foundations/semantic-tokens.
 *
 * Following jaen is not only for consistency: gatsby-plugin-jaen's system
 * reads `semanticTokens.colors.brand` off THIS system through the theme shadow
 * and merges it into the CMS's own. A value that diverges here silently
 * re-colours the CMS chrome.
 *
 * One caveat specific to this ramp. brand.900 is #18011a, nearly black, and
 * brand.950 (#47044e) is LIGHTER than the step above it. The scale is not
 * monotonic at the dark end. `subtle._dark` therefore takes 900 on purpose (it
 * wants to be the darkest thing available) while `muted._dark` takes 800,
 * which is where the ramp still behaves.
 */
const brandColorPalette = {
  solid: { value: { base: '{colors.brand.500}', _dark: '{colors.brand.200}' } },
  contrast: { value: { base: '{colors.white}', _dark: '{colors.gray.800}' } },
  fg: { value: { base: '{colors.brand.600}', _dark: '{colors.brand.200}' } },
  muted: { value: { base: '{colors.brand.100}', _dark: '{colors.brand.800}' } },
  subtle: { value: { base: '{colors.brand.50}', _dark: '{colors.brand.900}' } },
  emphasized: {
    value: { base: '{colors.brand.600}', _dark: '{colors.brand.300}' }
  },
  border: { value: { base: '{colors.brand.500}', _dark: '{colors.brand.400}' } },
  focusRing: {
    value: { base: '{colors.brand.500}', _dark: '{colors.brand.200}' }
  }
};

export const siteConfig = defineConfig({
  cssVarsPrefix: 'chakra',
  // dist/jaen.css still ships Tailwind's unlayered preflight, and an unlayered
  // normal declaration beats every cascade layer regardless of specificity.
  // Comes back out once the Tailwind bundle is gone.
  disableLayers: true,
  theme: {
    // v3 moved lg from 62em (992px) to 1024px. Pinned, or every responsive
    // array in the site shifts at a width nobody chose.
    breakpoints: {
      sm: '480px',
      md: '768px',
      lg: '992px',
      xl: '1280px',
      '2xl': '1536px'
    },
    tokens: {
      // The two helpers produce the shape v3 wants but cannot prove it to
      // TypeScript: Recursive<TokenSchema> is a conditional type over the
      // literal keys of the input, and both trees are built at runtime from
      // plain objects.
      /**
       * v2's `black`, pinned, then the site's own scales on top.
       *
       * @chakra-ui/theme's `black` was `#000000`; v3's is `#09090B`. colors.ts
       * declares neither, so every `black` call site would silently move three
       * per cent off the value v2 painted. `white` is `#FFFFFF` in both.
       */
      colors: {
        black: { value: '#000000' },
        ...toTokenScale(themeColors)
      } as never,
      /**
       * The site's three faces, declared in styles/global.css as @font-face
       * against the .ttf files in static/fonts. fonts.ts is not empty here, so
       * unlike netsnek.com there is nothing of Chakra's default stack to pin:
       * every value below is the site's own.
       */
      fonts: toTokenScale(themeFonts) as never,
      /**
       * v2's `borders` scale, pinned.
       *
       * v3 renamed it to xs/sm/md/lg/xl and dropped the pixel keys, so
       * `border="1px"` no longer resolves. The literal `1px` is emitted
       * instead, which resets border-style to its initial `none` and paints
       * nothing, and v3's preflight `border-style: solid` on `*` loses to the
       * shorthand.
       */
      borders: {
        '1px': { value: '1px solid' },
        '2px': { value: '2px solid' },
        '4px': { value: '4px solid' },
        '8px': { value: '8px solid' }
      },
      sizes: {
        /**
         * v3 dropped the `container.*` namespace, and an unresolvable size is
         * not an error: maxW would receive the literal string and the section
         * would go full-bleed. v2's four values.
         */
        container: {
          sm: { value: '640px' },
          md: { value: '768px' },
          lg: { value: '1024px' },
          xl: { value: '1280px' }
        }
      }
    },
    semanticTokens: {
      colors: {
        ...(toV3SemanticTokens(themeSemanticTokens.colors) as object),
        // `brand` is not one of the roots the site's own tree declares, so
        // this adds rather than overwrites.
        brand: brandColorPalette
      } as never
    },
    recipes: {
      button: buttonRecipe,
      container: containerRecipe,
      heading: headingRecipe,
      kbd: kbdRecipe,
      link: linkRecipe,
      separator: separatorRecipe,
      skeleton: skeletonRecipe
    },
    slotRecipes: {
      accordion: accordionSlotRecipe,
      alert: alertSlotRecipe,
      breadcrumb: breadcrumbSlotRecipe,
      list: listSlotRecipe,
      tooltip: tooltipSlotRecipe
    }
  }
});

/**
 * jaen's provider owns the reset and the shared `*` rules, and it is mounted on
 * every route. Stripping them from the base config here keeps the site's
 * provider emitting only what the site itself declares.
 */
const {
  globalCss: _jaenOwnsGlobals,
  preflight: _jaenOwnsPreflight,
  ...base
} = defaultConfig;

/** Mounted on site routes. Carries the site's globals. */
export const system = createSystem(
  base,
  siteConfig,
  defineConfig({
    preflight: false,
    globalCss: {
      /**
       * v2's reset, written out, because v3's is not a drop-in replacement.
       *
       * jaen's provider used to apply its reset to the whole document and now
       * scopes it to the CMS frame, which is correct: a CMS has no business
       * restyling the site around it. That leaves the site to supply its own,
       * and the honest one to supply is the one v2 actually had.
       *
       * The difference is one declaration and it is not cosmetic. v3's
       * preflight adds `font: inherit` to the universal rule. SVG presentation
       * attributes are author-origin with zero specificity, so they lose to any
       * CSS declaration at all: every inline SVG that sizes its text with a
       * `font-size` attribute has it overridden by the inherited 16px. This
       * site draws two logos and a vCard QR that way.
       *
       * @chakra-ui/css-reset's own rule, verbatim.
       */
      '*, *::before, *::after': {
        borderWidth: '0',
        borderStyle: 'solid',
        boxSizing: 'border-box',
        wordWrap: 'break-word'
      },

      /**
       * The replacement for `withDefaultColorScheme({colorScheme: 'brand'})`,
       * which v3 dropped with no equivalent.
       *
       * This is the largest silent-failure risk in the whole migration. The
       * site source contains no colorScheme prop at all: every Button, Input
       * and Checkbox is brand-violet by default. Without this line they all
       * revert to v3's grey, and both tsc and gatsby build stay green while
       * they do it.
       */
      html: { colorPalette: 'brand' },
      /**
       * v2's body rule, restored.
       *
       * On v2 site routes this came from `<GlobalStyle/>` inside the site's own
       * emotion ThemeProvider, rendering @chakra-ui/theme's default
       * `styles.global.body`.
       *
       * `fontFamily` is the one that matters most. Without it the site inherits
       * the `html { font-family: ... }` that jaen's preflight sets, which is
       * jaen's own face rather than Cabin, for every word on every site route.
       *
       * `chakra-body-bg` is `{white, _dark: gray.800}`, which is what
       * `shared.body.bgColor` already is. `chakra-body-text` is
       * `{gray.800, _dark: whiteAlpha.900}`; the body takes that pair directly
       * rather than going through a semantic token whose dark half differs.
       *
       * lineHeight is not restored: v2 set `lineHeights.base` (1.5) here, and
       * v3's preflight already puts 1.5 on `html` with `* { font: inherit }`
       * underneath it.
       */
      body: {
        fontFamily: 'body',
        bg: 'shared.body.bgColor',
        color: { base: 'gray.800', _dark: 'whiteAlpha.900' },
        transitionProperty: 'background-color',
        // v2's `durations.normal`. v3's scale renamed it away, so the value is
        // spelled out.
        transitionDuration: '200ms'
      }
    }
  })
);

/** The v3 spelling of v2's bare <ThemeProvider>: tokens, no globals. */
export const chromeSystem = createSystem(base, siteConfig);
